<template>
  <div class="matrix-board-wrapper" :style="{ height: boardMaxHeight }" v-if="!(boardData?.real === null && boardData?.avg === null) && !(boardQuestions?.real === null && boardQuestions?.avg === null) && boardSetup && entities && entities.length">

    <!-- Sticky Header-Leiste: bleibt beim Scrollen oben kleben  && boardQuestions  -->
    <div class="board-header-sticky board-header-background">
      <div class="corner-header">
        <div class="header-row header-row-entities">{{ $t('Webportal.Matrix.TableHeader') }}</div>
        <div class="header-row header-row-waves"></div>
      </div>
      <div class="header-entities-viewport">
        <div class="header-entities-scroll" ref="headerScroll">
          <div  v-for="entity in entities" :key="'header-' + entity.id" class="entity-header-group"
                :class="{ 'entity-average': entity.isAverage, 'entity-noflag': !showFlags }">
            <!-- Zeile 1: Entity-Name -->
            <div class="header-row header-row-entities" :class="{ 'header-row-compress': entity.waves.length <= 1, 'header-row-break': !showFlagsOfEntity(entity) }">
              <span v-if="showFlagsOfEntity(entity)" v-for="code in getRegions(entity.label)" :key="code"
                    class="flag-icon" :class="'flag-icon-' + code"></span>
              <span class="entity-name" :style="{ maxWidth: (entity.waves.length * 4) + 'rem' }" v-b-tooltip.hover.bottom="entity.label">{{ entity.label }}</span>
            </div>
            <!-- Zeile 2: Wellen -->
            <div class="waves-header-row">
              <div v-for="wave in entity.waves" :key="wave.id"
                   class="wave-header"
                   :class="{ 'is-hovered-col': hoveredCol === entity.id + '-' + wave.id }">
                <div>{{ wave.label }}</div>
                <div class="wave-header-n">n={{ wave.SurveyInstanceCount || 'N/A' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Body: KPI-Spalte + Datenspalten ohne Header -->
    <div class="matrix-board-container">
      <!-- Fixierte erste Spalte mit KPI-Hierarchie -->
      <div class="kpi-column-fixed board-header-background" ref="kpiColumn" @scroll="onKpiScroll">
        <MatrixKpiRow v-for="row in rowItems" :key="row.id" :item="row" :depth="1" />
      </div>

      <!-- Scrollbare Datenspalten -->
      <div class="data-columns-wrapper" ref="dataWrapper" @scroll="onDataScroll">
        <div class="data-columns-scroll">
          <div class="data-rows-container">
            <MatrixDataSection v-for="row in rowItems" :key="'data-' + row.id" :item="row" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MatrixKpiRow from './MatrixKpiRow.vue';
import MatrixDataSection from './MatrixDataSection.vue';
import { getRegions } from '@/utils/regionHelpers';
import { mapGetters } from 'vuex';

export default {
  name: 'MatrixBoard',

  components: {
    MatrixKpiRow,
    MatrixDataSection
  },

  props: {
    boardData:          { type: Object, required: true },
    boardQuestions:     { type: Object, required: true },
    boardSetup:         { type: Object, required: true },
    entities:           { type: Array, required: true },
    filters:            { type: Object, default: null },
    selectedMode:       { type: String, default: 'all' },
    showFlags:          { type: Boolean, default: true },
    selectedFilter:     { type: String, default: null },
  },

  computed: {
    ...mapGetters('clientData', ['indices']),
    legends() {
      return this.indices;
    },

    rowItems() {
      return this.boardSetup && this.boardSetup.boards
        ? this.boardSetup.boards[0].controls.filter(c => !c.hidden)
        : [];
    },

    qaNodeMap() {
      const map = {};
      const traverse = (items) => {
        for (const item of items) {
          if (item.mode === 'question' || item.mode === 'answer') {
            map[item.id] = item;
          }
          if (item.children) traverse(item.children);
        }
      };
      traverse(this.rowItems);
      return map;
    },

    kpiNodeMap() {
      const map = {};
      const traverse = (items) => {
        for (const item of items) {
          if (item.mode === 'kpi') {
            map[item.id] = item;
          }
          if (item.children) traverse(item.children);
        }
      };
      traverse(this.rowItems);
      return map;
    },

    boardDataMap() {
      const map = {};
      for (const d of this.boardData) {
        if (d?.controlId) map[d.controlId] = d;
      }
      return map;
    },
  },

  provide() {
    return {
      boardGetHoveredRow: () => this.hoveredRow,
      boardGetHoveredCol: () => this.hoveredCol,
      boardGetEntities:   () => this.entities,
      boardGetData:       this.getData,
      boardSetHover:      this.setHover,
      boardClearHover:    this.clearHover,
      boardToggleRow:     this.toggleRow,
    };
  },

  data() {
    return {
      hoveredRow: null,
      hoveredCol: null,
      selectedRow: null,
      selectedCell: null,
      hoverEnabled: false,
      boardMaxHeight: null,
      syncingScroll: false,
    };
  },

  watch: {
    selectedMode: {
      handler(newMode) {
        this.applyExpandMode(newMode);
      },
      immediate: true
    },
    boardData: {
      handler() {
        this.$nextTick(() => {
          this.applyExpandMode(this.selectedMode);
        });
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.calcMaxHeight();
      this.syncScrollbarCompensation();
    });
    window.addEventListener('resize', this.calcMaxHeight);
    this._resizeObserver = new ResizeObserver(() => this.syncScrollbarCompensation());
    if (this.$refs.dataWrapper) this._resizeObserver.observe(this.$refs.dataWrapper);
  },

  updated() {
    if (!this.boardMaxHeight) {
      this.$nextTick(() => this.calcMaxHeight());
    }
    this.$nextTick(() => this.syncScrollbarCompensation());
  },

  beforeUnmount() {
    this.clearHover();
    window.removeEventListener('resize', this.calcMaxHeight);
    if (this._resizeObserver) this._resizeObserver.disconnect();
  },

  methods: {
    getRegions,

    calcMaxHeight() {
      const el = this.$el;
      if (!el || !el.getBoundingClientRect) return;
      const top = el.getBoundingClientRect().top;
      this.boardMaxHeight = (window.innerHeight - top - 40) + 'px';
    },

    syncScrollbarCompensation() {
      const wrapper = this.$refs.dataWrapper;
      const kpi = this.$refs.kpiColumn;
      if (!wrapper || !kpi) return;
      const scrollbarH = wrapper.offsetHeight - wrapper.clientHeight;
      kpi.style.paddingBottom = scrollbarH > 0 ? scrollbarH + 'px' : '';
    },

    showFlagsOfEntity(entity) {
      if (entity.isAverage) return false;

      return this.showFlags;
    },


    onDataScroll(e) {
      if (this.$refs.headerScroll) {
        this.$refs.headerScroll.style.transform = `translateX(-${e.target.scrollLeft}px)`;
      }
      if (this.syncingScroll) return;
      this.syncingScroll = true;
      if (this.$refs.kpiColumn) {
        this.$refs.kpiColumn.scrollTop = e.target.scrollTop;
      }
      this.syncingScroll = false;
    },

    onKpiScroll(e) {
      if (this.syncingScroll) return;
      this.syncingScroll = true;
      if (this.$refs.dataWrapper) {
        this.$refs.dataWrapper.scrollTop = e.target.scrollTop;
      }
      this.syncingScroll = false;
    },

    toggleRow(rowId) {
      const toggleInTree = (items) => {
        for (const item of items) {
          if (item.id === rowId) {
            item.isOpen = !item.isOpen;
            return true;
          }
          if (item.children && toggleInTree(item.children)) {
            return true;
          }
        }
        return false;
      };
      toggleInTree(this.rowItems);
    },

    getData(kpi, _entityId, waveId, item) {
      _entityId = String(_entityId);
      let indexId = item?.indexId;
      if (item && item.ShowKpiLabelMode) {
        return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
      }

      if (_entityId === '__average__') {
        let itemData = this.getAverageData(kpi, waveId, indexId);
        return itemData;
      }

      // Question and Answers: wenn qaNode aus qaNodeMap NOT NULL
      const qaNode = this.qaNodeMap[kpi];
      if (qaNode) {
        const qitem = this.getQItem("real", qaNode);
        if (!qitem) {
          return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
        }

        const qIdx = kpi.startsWith('q_') ? +kpi.split('_').pop() : +kpi.split('_').at(-2);
        var questionKpi = qitem.Questions[qIdx];
        var question = questionKpi?.Waves.find(w => w.SurveyWaveName === waveId && _entityId.includes(w.AggregationLevel2Value));
        if (!question) {
          return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
        }
        if (qaNode.mode === 'question') {
          return { value: '\u00A0', n: question.QuestionSurveyInstanceCount ?? '\u00A0', delta: '\u00A0' };
        }
        if (qaNode.mode === 'answer') {
          let answer = questionKpi.Answers[+kpi.split('_').pop()];
          if (!answer || !answer.Waves || answer.Waves.length === 0) {
            return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
          }
          let answerData = answer.Waves.find(w => w.SurveyWaveName === waveId && _entityId.includes(w.AggregationLevel2Value));
          if (!answerData) {
            return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
          }
          return { value: answerData.CountPercent ?? '\u00A0', n: answerData.SurveyInstanceCount ?? '\u00A0', delta: '\u00A0' };
        }
        log.error("MatrixBoard.getData: Question and Answers yielded n/thn");
        return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
      }

      // KPI: Aus averageDataMap nachschlagen
      const kpiNode = this.kpiNodeMap[kpi];
      if(!kpi || !kpiNode) return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' }; // { value: 'N/A', n: 'N/A', delta: 'N/A' };
      let kitem = null;
      if (kpiNode.isKpiGroup) {
         kitem = this.boardData.real?.KPIGroup?.find(x => x.KpiCode === kpiNode.kpiCode);
      } else {
        kitem = this.boardData.real?.KPI?.find(x => x.KpiCode === kpiNode.kpiCode);
      }
      if (!kitem) {
        return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
      }

      const waveData = kitem.SurveyWave.find(w => w.Name === waveId && _entityId.includes(w.AggregationLevel2));
      if (!waveData || (!waveData.Result && waveData.SurveyInstanceCount)) {
        return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
      }

      // Delta zur Vorwelle berechnen (Wellen sind in entity.waves aufsteigend nach SortOrder sortiert)
      let delta = null;
      const entity = this.entities.find(e => e.id === _entityId);
      if (entity) {
        const waveIndex = entity.waves.findIndex(w => w.label === waveId);
        if (waveIndex === 0) {
          delta = '\u00A0';
        } else if (waveIndex > 0) {
          const prevWaveLabel = entity.waves[waveIndex - 1].label;
          const prevWaveData = kitem.SurveyWave.find(w => w.Name === prevWaveLabel && _entityId.includes(w.AggregationLevel2));
          if (waveData.Result != null && prevWaveData?.Result != null) {
            delta = Math.round((waveData.Result - prevWaveData.Result) * 10) / 10;
          }
        }
      }

      let result = {
        value: waveData.Result,
        n: waveData.SurveyInstanceCount,
        delta
      };
      return this.applyPerformanceIndex(result, item, indexId);
    },

    applyPerformanceIndex(result, data, indexId) {
      if (typeof result?.value !== 'number') return result;

      let thresholds = this.legends.find(idx => idx.indexId === indexId)?.thresholds;
      if (!thresholds) return result;
      const thre = thresholds.find(th => result.value >= th.min && result.value <= th.max);
      result.colorClass = thre?.class;
      result.color = thre?.color;

      return result;
    },

    getQItem(prop, qaNode) {
      if (qaNode.isKpiGroup) {
        return this.boardQuestions[prop]?.KPIGroup?.find(x => x.KpiCode === qaNode.kpiCode);
      } else {
        return this.boardQuestions[prop]?.KPI?.find(x => x.KpiCode === qaNode.kpiCode);
      }
    },

    getAverageData(kpi, waveId, indexId) {
      // Question and Answers: wenn qaNode aus qaNodeMap NOT NULL
      const qaNode = this.qaNodeMap[kpi];
      if (qaNode) {
        const qitem = this.getQItem("avg", qaNode);
        if (!qitem) {
          return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
        }

        const qIdx = kpi.startsWith('q_') ? +kpi.split('_').pop() : +kpi.split('_').at(-2);
        var questionKpi = qitem.Questions[qIdx];
        var question = questionKpi?.Waves.find(w => w.SurveyWaveName === waveId);
        if (!question) {
          return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
        }
        if (qaNode.mode === 'question') {
          return { value: '\u00A0', n: question.QuestionSurveyInstanceCount ?? '\u00A0', delta: null };
        }
        if (qaNode.mode === 'answer') {
          let answer = questionKpi.Answers[+kpi.split('_').pop()];
          if (!answer || !answer.Waves || answer.Waves.length === 0) {
            return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
          }
          let answerData = answer.Waves.find(w => w.SurveyWaveName === waveId);
          if (!answerData) {
            return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
          }
          return { value: answerData.CountPercent, n: answerData.SurveyInstanceCount, delta: null };
        }
        log.error("MatrixBoard.getData: Question and Answers yielded n/thn");
        return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
      }

      // KPI: Aus averageDataMap nachschlagen
      const kpiNode = this.kpiNodeMap[kpi];
      if (!kpi || !kpiNode) { return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' }; } // return { value: 'N/A', n: 'N/A', delta: 'N/A' };
      let kitem = null;
      if (kpiNode.isKpiGroup) {
         kitem = this.boardData.avg?.KPIGroup?.find(x => x.KpiCode === kpiNode.kpiCode);
      } else {
        kitem = this.boardData.avg?.KPI?.find(x => x.KpiCode === kpiNode.kpiCode);
      }
      if (!kitem) {
        return { value: '\u00A0', n: '\u00A0', delta: '\u00A0' };
      }

      // Fuer Durchschnitt: erstes SurveyWave-Match nach waveId (ohne AggregationLevel2-Filter)
      const waveData = kitem.SurveyWave.find(w => w.Name === waveId);

      // Delta zur Vorwelle berechnen
      let delta = null;
      const avgEntity = this.entities.find(e => e.isAverage);
      if (avgEntity) {
        const waveIndex = avgEntity.waves.findIndex(w => w.label === waveId);
        if (waveIndex === 0) {
          delta = '\u00A0';
        } else if (waveIndex > 0) {
          const prevWaveLabel = avgEntity.waves[waveIndex - 1].label;
          const prevWaveData = kitem.SurveyWave.find(w => w.Name === prevWaveLabel);
          if (waveData?.Result != null && prevWaveData?.Result != null) {
            delta = Math.round((waveData.Result - prevWaveData.Result) * 10) / 10;
          }
        }
      }

      let result = {
        value: waveData?.Result ?? '\u00A0',
        n: waveData?.SurveyInstanceCount ?? '\u00A0',
        delta: delta ?? '\u00A0'
      };
      return this.applyPerformanceIndex(result, kpiNode, indexId);
    },

    setHover(colId, rowId) {
      if (!this.hoverEnabled) return;
      this.hoveredCol = colId;
      this.hoveredRow = rowId;
    },

    clearHover() {
      this.hoveredCol = null;
      this.hoveredRow = null;
    },

    applyExpandMode(mode) {
      // Funktion zum Finden der Tiefe der letzten KPI-Ebene in einem Pfad
      const findLastKpiDepth = (items, currentDepth = 0) => {
        let maxKpiDepth = currentDepth;

        for (const item of items) {
          if (item.mode === 'kpi' && item.children) {
            // Prüfe, ob alle Kinder questions/answers sind
            const hasKpiChild = item.children.some(child => child.mode === 'kpi');

            if (!hasKpiChild) {
              // Dies ist die letzte KPI-Ebene
              maxKpiDepth = Math.max(maxKpiDepth, currentDepth);
            } else {
              // Rekursiv weitersuchen
              const childDepth = findLastKpiDepth(item.children, currentDepth + 1);
              maxKpiDepth = Math.max(maxKpiDepth, childDepth);
            }
          }
        }

        return maxKpiDepth;
      };

      // Funktion zum Setzen des isOpen Status basierend auf Modus
      const setOpenState = (items, currentDepth = 0, lastKpiDepth = 0) => {
        for (const item of items) {
          if (mode === 'minimal') {
            // Alles zuklappen
            item.isOpen = false;
          } else if (mode === 'all') {
            // Alles aufklappen (nur wenn Kinder existieren)
            item.isOpen = item.children ? true : false;
          } else if (mode === 'medium') {
            // Bis auf die letzte KPI-Ebene alles aufklappen
            if (item.mode === 'kpi') {
              const hasKpiChild = item.children && item.children.some(child => child.mode === 'kpi');

              if (!hasKpiChild) {
                // Dies ist die letzte KPI-Ebene - zuklappen
                item.isOpen = false;
              } else {
                // Frühere KPI-Ebene - aufklappen
                item.isOpen = true;
              }
            } else {
              // Questions und Answers zuklappen
              item.isOpen = false;
            }
          }

          // Rekursiv für Kinder anwenden
          if (item.children) {
            setOpenState(item.children, currentDepth + 1, lastKpiDepth);
          }
        }
      };

      // Finde die Tiefe der letzten KPI-Ebene (nur für medium Modus relevant)
      const lastKpiDepth = mode === 'medium' ? findLastKpiDepth(this.rowItems) : 0;

      // Wende den Modus an
      setOpenState(this.rowItems, 0, lastKpiDepth);
    }
  }
};
</script>

<style lang="scss" scoped>
.matrix-board-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--custom-matrix-bg);
}

.board-header-background {
  background-color: var(--custom-black-05);
  [data-bs-theme="dark"] & {
    background-color: var(--custom-matrix-bg);
  }
}

/* Header-Leiste: fixiert oben im Container */
.board-header-sticky {
  flex-shrink: 0;
  z-index: 11;
  display: flex;
  border-bottom: 1px solid var(--bs-border-color);
}

.header-entities-viewport {
  flex: 1;
  overflow: hidden;
}

.header-entities-scroll {
  display: flex;
  gap: 0;
  will-change: transform;
}

.matrix-board-container {
  display: flex;
  gap: 0;
  position: relative;
  flex: 1;
  overflow: hidden;
}

/* Fixierte linke Spalte */
.kpi-column-fixed {
  min-width: 18rem;
  max-width: 18rem;
  flex-shrink: 0;
  z-index: 10;
  border-right: 1px solid var(--bs-border-color);
  overflow-y: scroll;
  scrollbar-width: none; /* Scrollbar verstecken, wird per JS synchronisiert */
}

.corner-header {
  display: flex;
  flex-direction: column;
  min-width: 18rem;
  max-width: 18rem;
  flex-shrink: 0;
  border-right: 1px solid var(--bs-border-color);
}

.header-row {
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--bs-secondary-color);
}

.header-row-compress {
  padding: 0rem!important;
  gap: 0rem!important;
  font-size: var(--font-10) !important;
}
.header-row-entities {
  min-height: 3rem;
  display: flex;
  align-items: center;
  color: var(--color-foreground);
  font-weight: 300;
}
.header-row-break {
  padding: 0 !important;
  justify-content: center !important;
}
.header-row-break .entity-name {
  white-space: normal !important;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  text-align: center;
}
.header-row-waves {
  height: 2.5rem;
  /* border-top: 1px solid var(--bs-border-color); */
}

/* Scrollbare Datenspalten */
.data-columns-wrapper {
  flex: 1;
  overflow-x: auto;
  overflow-y: scroll;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: var(--custom-black-20) transparent;

  [data-bs-theme="dark"] & {
    scrollbar-color: var(--custom-white-20) transparent;
  }
}

.data-columns-scroll {
  min-width: max-content;
}

.entity-header-group {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--bs-border-color);
}

.entity-header-group:last-child {
  border-right: none;
}

.entity-header-group .header-row-entities {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 12px;
  border-bottom: 1px solid var(--bs-border-color);
}

.flag-icon {
  width: 20px;
  height: 15px;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.entity-name {
  white-space: nowrap;
  color: var(--color-foreground);
  font-weight: 300;
}

.waves-header-row {
  display: flex;
  height: 2.5rem;
  gap: 0;
}

.wave-header {
  min-width: 5rem;
  padding: 0.5rem 0.75rem;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  border-right: 1px solid var(--bs-border-color);
  color: var(--color-foreground);
  transition: background-color 0.2s ease;
}

.wave-header-n {
  text-align: center;
  font-size: 10px;
  color: var(--color-muted-foreground);
  font-weight: 300;
}

.wave-header:last-child {
  border-right: none;
}

/* Daten-Zeilen */
.data-rows-container {
  display: flex;
  flex-direction: column;
  background-color:  var(--custom-matrix-inner-table-bg);
}

/* Average Entity Styling */
.entity-noflag,
.entity-average {
  background-color: color-mix(in srgb, var(--bs-border-color) 10%, transparent);
  border-right: 2px solid var(--bs-border-color);
}
.entity-noflag .entity-name,
.entity-average .entity-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Hover State für Wave-Header */
.is-hovered-col {
  background-color: color-mix(in srgb, var(--bs-border-color) 20%, transparent) !important;
}
</style>
